/* eslint-disable camelcase */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import utils from "@utils";
import { GenericService, JobService } from "@services";
import classNames from "classnames";
import GoogleMap from "google-map-react";
import { GOOGLE_MAP_SETTINGS, GOOGLE_MAP_OPTIONS } from "@constants";
import { Image, MapMarker, Modal, Loader } from "../../../../../components";
import "./JobActivityLogModal.scss";

const JobActivityLogModal = props => {
	const { lang } = global;
	const {
		show,
		onHide,
		clockInTime,
		clockOutTime,
		officer,
		officerId,
		user,
		jobEventTimeZoneId,
		positionId,
		positionTitle,
	} = props;

	const [activeTab, setActiveTab] = useState("activity");
	const [filter, setFilter] = useState("all");
	const [jobData, setJobData] = useState({});
	const [mapPoints, setMapPoints] = useState([]);
	const [filteredPoints, setFilteredPoints] = useState([]);
	const [mapCenter, setMapCenter] = useState({});
	const [tabOptions, setTabOptions] = useState({});
	const [message, setMessage] = useState("");
	const [highlightedPoint, setHighlightedPoint] = useState("");
	const [activityLogs, setActivityLogs] = useState([]);
	const [jobTimeZone, setJobTimeZone] = useState(null);
	const [searchOfficer, setSearchOfficer] = useState(null);
	const [searchOfficerLoading, setSearchOfficerLoading] = useState(false);

	useEffect(() => {
		if (searchOfficer && positionId && positionTitle) {
			JobService.getCheckIns(positionId, searchOfficer.UserId, () => {})
				.then(({ data }) => {
					const { CheckIns, JobLocation, StartDateTime, EndDateTime } = data;
					const checkins = CheckIns.filter(item => item.IsCheckin).map(item => ({
						...item,
						type: "checkin",
						name: lang.t("Clocked In"),
					}));
					const checkouts = CheckIns.filter(item => item.CheckoutLatitude !== null).map(
						item => ({ ...item, type: "checkout", name: lang.t("Clocked Out") }),
					);
					const pings = CheckIns.filter(item => item.Type === 1).map(item => ({
						...item,
						type: "ping",
						name: lang.t("Ping"),
					}));

					const tabOptionsResponse = [{ filter: "all", label: lang.t("All") }];
					if (checkins.length > 0 || checkouts.length > 0) {
						tabOptionsResponse.push({
							filter: "checkin",
							label: lang.t("Clock In/Out"),
						});
					}
					if (pings.length > 0) {
						tabOptionsResponse.push({ filter: "ping", label: lang.t("Pings") });
					}

					const jobDataResponse = {
						name: JobLocation.Name,
						startUTC: `${StartDateTime}Z`,
						endUTC: `${EndDateTime}Z`,
						location: { lat: JobLocation.Latitude, lng: JobLocation.Longitude },
					};

					const pointsArray = [
						{
							type: "job",
							name: jobDataResponse.name,
							location: jobDataResponse.location,
						},
						...checkins,
						...pings,
						...checkouts,
					];
					const mapPointsResponse = pointsArray.map((item, index) => {
						if (item.type === "job") return { ...item, id: "Job Location" };

						const result = {
							id: `jobpoint-${index}`,
							type: item.type,
							name: item.name,
							responded: item.PingRequestStatus !== 3,
							errorMsg: item.PingErros?.length > 0 ? item.PingErros[0] : "",
							dateTimeUTC: `${item.CheckinDateTimeUTC}Z`,
							location: { lat: item.Latitude, lng: item.Longitude },
							distance: item.DistanceFromJob,
						};

						if (item.type === "checkout") {
							result.dateTimeUTC = `${item.CheckoutDateTimeUTC}Z`;
							result.location = {
								lat: item.CheckoutLatitude,
								lng: item.CheckoutLongitude,
							};
							result.distance = item.DistanceFromJobCheckOut;
						}

						return result;
					});

					setMapPoints(mapPointsResponse);
					setFilteredPoints(mapPointsResponse);
					setTabOptions(tabOptionsResponse);
					setJobData(jobDataResponse);
					setMapCenter(jobDataResponse.location);
				})
				.catch(() => {
					utils.notify({
						type: "error",
						message: lang.t("Error loading Job Points"),
						timeOut: 2000,
					});
				});

			JobService.getJobActivityLog(positionId, searchOfficer.UserId)
				.then(({ data }) => {
					setActivityLogs(data);
				})
				.catch(() => {
					utils.notify({
						type: "error",
						message: lang.t("Error loading Activity Log"),
						timeOut: 2000,
					});
				});
		}
	}, [positionId, positionTitle, searchOfficer]);

	useEffect(() => {
		if (officer) {
			setSearchOfficer(officer);
		}
	}, [officer]);

	useEffect(() => {
		if (!officer && officerId && positionId && positionTitle) {
			setSearchOfficerLoading(true);
			JobService.getAssignedOfficersByPositionId(positionId)
				.then(({ data }) => {
					const { AssignedOfficers } = data;
					const officerData = AssignedOfficers.find(item => item.UserId === officerId);
					setSearchOfficer(officerData);
				})
				.catch(() => {
					utils.notify({
						type: "error",
						message: lang.t("Error loading Assigned Officers"),
						timeOut: 2000,
					});
				})
				.finally(() => {
					setSearchOfficerLoading(false);
				});
		}
	}, [officerId, positionId, positionTitle]);

	useEffect(() => {
		GenericService.getTimeZones()
			.then(({ data }) => {
				const timeZone = data.find(item => item.Id === jobEventTimeZoneId);
				setJobTimeZone(timeZone);
			})
			.catch(({ response }) => {
				utils.notify({
					type: "error",
					message: response.data.Message || lang.t("Error loading Time Zones"),
				});
			});
	}, [jobEventTimeZoneId]);

	const highlightPoint =
		point =>
		({ key }) => {
			if (!key || (key && key === "Enter")) {
				setMapCenter(point.location);
				setMessage(
					utils.formatDate(
						new Date(point?.dateTimeUTC),
						"en-US",
						jobEventTimeZoneId,
						"dt-short",
					) || null,
				);
				setHighlightedPoint(point.id);
			}
		};

	const filterPoints = newFilter => {
		let newMapCenter = GOOGLE_MAP_SETTINGS.defaultCenter;
		let newFilteredPoints = mapPoints;

		if (newFilter === "all") {
			newMapCenter = { ...jobData.location };
		} else if (newFilter === "checkin") {
			newFilteredPoints = mapPoints.filter(point => point.type !== "ping");
		} else {
			newFilteredPoints = mapPoints.filter(
				point => point.type === "job" || point.type === "ping",
			);
		}

		if (mapPoints.findIndex(point => point.type === newFilter) !== -1) {
			newMapCenter = mapPoints.find(point => point.type === newFilter).location;
		}

		setMapCenter(newMapCenter);
		setFilteredPoints(newFilteredPoints);
		setFilter(newFilter);
	};

	const processTimeFormat = item => {
		const auxS = item.split("-");
		if (auxS.length === 2 && user.UserPreferences === "useMilitaryTime") {
			const aux0 = auxS[0].slice(-12).split(":");
			const finalS0 = `${aux0[0]}:${aux0[1]} ${aux0[2].slice(-3)}`;
			const mil0 = utils.convertTime12to24(finalS0.trim());
			const aux1 = auxS[1].split(":");
			const finalS1 = `${aux1[0]}:${aux1[1]}${aux1[2].slice(-3)}`;
			const mil1 = utils.convertTime12to24(finalS1.trim());
			return `${item.replace(item.slice(-26), "")} ${mil0} - ${mil1}`;
		}
		return item;
	};

	const toggleShowDetails = activity => {
		const newActivityLogs = activityLogs.map(item => {
			if (item.Id === activity.Id) {
				return { ...item, showDetails: !item.showDetails };
			}
			return item;
		});
		setActivityLogs(newActivityLogs);
	};

	return (
		<Modal
			id="activity-log-modal"
			size="lg"
			show={show}
			onHide={() => onHide()}
		>
			<Modal.Header
				closeButton
				className="hidden-print"
			>
				<Modal.Title>{lang.t("Job Activity")}</Modal.Title>
			</Modal.Header>
			<Modal.Body className="container-fluid">
				<div>
					<div className="row">
						<div className="col-xs-12">
							<div className="profile-container">
								<div>
									<Image
										className="picture"
										src={searchOfficer?.OfficerProfile?.ContactPhotoUrl || ""}
										fallbackSrc="avatar"
									/>
								</div>
								<div className="officer-details">
									<span className="subtitle">
										{`${searchOfficer?.OfficerProfile?.FirstName} ${searchOfficer?.OfficerProfile?.LastName}, ${searchOfficer?.OfficerProfile?.OfficerRank}`}
									</span>
									<span className="description">
										{searchOfficer?.OrganizationName ||
											searchOfficer?.OfficerProfile?.JurisdictionName}
									</span>
									<span className="description">
										{`Position: ${positionTitle}`}
									</span>
								</div>
								<div className="job-details">
									<span className="subtitle">Scheduled Date</span>
									<span className="description">
										{utils.formatDate(
											new Date(jobData.startUTC),
											"en-US",
											jobEventTimeZoneId,
											"2-digit",
										)}
									</span>
									<span className="subtitle">Scheduled Time</span>
									<span className="description">
										{utils.formatTime(
											new Date(jobData.startUTC),
											user.UserPreferences !== "useMilitaryTime",
											jobEventTimeZoneId,
										)}
										&nbsp;-&nbsp;
										{utils.formatTime(
											new Date(jobData.endUTC),
											user.UserPreferences !== "useMilitaryTime",
											jobEventTimeZoneId,
										)}
										&nbsp;
										{jobTimeZone?.Description?.split("-")?.[0] || ""}
									</span>
									<span className="subtitle">Actual Clock In/Out Time</span>
									<span className="description">
										<If condition={clockInTime || clockOutTime}>
											{`${clockInTime}-${clockOutTime}`}
										</If>
										<If condition={!clockInTime && !clockOutTime}>
											{lang.t("No Clock-In")}
										</If>
									</span>
								</div>
							</div>
						</div>
					</div>
					<div className="row tabs">
						<div className="col-xs-6 col-sm-4">
							<button
								id="activity-tab"
								type="button"
								className={classNames("btn btn-link btn-block", {
									active: activeTab === "activity",
								})}
								onClick={() => setActiveTab("activity")}
							>
								{lang.t("Activity Log")}
							</button>
						</div>
						<div className="col-xs-6 col-sm-4">
							<button
								id="map-tab"
								type="button"
								className={classNames("btn btn-link btn-block", {
									active: activeTab === "map",
								})}
								onClick={() => setActiveTab("map")}
							>
								{lang.t("Map")}
							</button>
						</div>
					</div>
					<If condition={activeTab === "activity"}>
						<div className="body-container">
							<div className="row column-headers v-align">
								<div className="col-sm-2">
									<strong>{lang.t("Date")}</strong>
								</div>
								<div className="col-sm-2">
									<strong>{lang.t("Time")}</strong>
								</div>
								<div className="col-sm-8">
									<strong>{lang.t("Description")}</strong>
								</div>
							</div>
							<For
								each="activity"
								of={activityLogs}
							>
								<div
									key={`activity-${activity.Id}`}
									className="row activity-row"
								>
									<div className="col-sm-2">
										<span>
											{utils.formatDate(
												new Date(`${activity.CreateDate}Z`),
												"en-US",
												jobEventTimeZoneId,
												"2-digit",
											)}
										</span>
									</div>
									<div className="col-sm-2">
										<span>
											{utils.formatTime(
												new Date(`${activity.CreateDate}Z`),
												user.UserPreferences !== "useMilitaryTime",
												jobEventTimeZoneId,
												true,
											)}
										</span>
									</div>
									<div className="col-sm-8">
										<div className="detailed-column">
											<div className="detail-container">
												<span>
													{processTimeFormat(activity.ActivityLogType)}
												</span>
												<If condition={activity.ActivityLogDescription}>
													<button
														type="button"
														className="btn btn-link text-left"
														onClick={() => toggleShowDetails(activity)}
													>
														{lang.t("Details")}
														<i
															title="View Details"
															className={classNames(
																"rkf chevron-icon",
																{
																	"rkf-chevron-down":
																		!activity.showDetails,
																	"rkf-chevron-up":
																		activity.showDetails,
																},
															)}
														/>
													</button>
												</If>
											</div>
											<If condition={activity.showDetails}>
												{activity.ActivityLogDescription.map(item => (
													<span key={`${activity.Id}-${item}`}>
														{item}
													</span>
												))}
											</If>
										</div>
									</div>
								</div>
							</For>
						</div>
					</If>
					<If condition={activeTab === "map"}>
						<div className="row">
							<div className="col-sm-6 col-md-7">
								<div className="pingmap-container">
									<GoogleMap
										zoom={15}
										center={mapCenter}
										bootstrapURLKeys={{ key: GOOGLE_MAP_SETTINGS.key }}
										options={GOOGLE_MAP_OPTIONS}
									>
										<For
											each="marker"
											of={filteredPoints}
										>
											<MapMarker
												key={marker.id}
												type={marker.type}
												lat={marker.location?.lat || 0}
												lng={marker.location?.lng || 0}
												highlighted={highlightedPoint === marker.id}
												message={message}
											/>
										</For>
									</GoogleMap>
								</div>
							</div>
							<div className="col-sm-6 col-md-4 col-md-offset-1">
								<div className="row tabs">
									<For
										each="tab"
										of={tabOptions}
									>
										<div
											key={`ping-tab-${tab.filter}`}
											className={`col-xs-${12 / tabOptions.length}`}
										>
											<button
												type="button"
												className={classNames("btn btn-link btn-block", {
													active: filter === tab.filter,
												})}
												onClick={() => filterPoints(tab.filter)}
											>
												{tab.label}
											</button>
										</div>
									</For>
								</div>
								<div className="container-fluid point-list">
									<For
										each="point"
										of={filteredPoints}
									>
										<div
											key={`list-${point.id}`}
											className="row"
											tabIndex="0"
											role="button"
											onClick={highlightPoint(point)}
											onKeyPress={highlightPoint(point)}
										>
											<div className="col-xs-2">
												<span
													className={`map-marker ${point.type}-marker`}
												/>
											</div>
											<div className="col-xs-10">
												<Choose>
													<When condition={point.type === "job"}>
														<strong>{lang.t("Job Site")}</strong>
														<div className="truncate">{point.name}</div>
													</When>
													<Otherwise>
														<div>
															<If
																condition={
																	point.type === "checkin" ||
																	point.type === "checkout"
																}
															>
																<strong>{point.name}:&nbsp;</strong>
															</If>
															{utils.formatDate(
																new Date(point.dateTimeUTC),
																"en-US",
																jobEventTimeZoneId,
																user.UserPreferences ===
																	"useMilitaryTime"
																	? "dt-num-mil"
																	: "dt-num",
															)}
														</div>
														<div>
															<Choose>
																{/* status of 3 indicates no response from the device */}
																<When
																	condition={
																		point.responded === 3
																	}
																>
																	<strong>
																		{lang.t("No response")}
																	</strong>
																</When>
																<When condition={point.errorMsg}>
																	<strong>
																		{point.errorMsg}
																	</strong>
																</When>
																<Otherwise>
																	{/* eslint-disable-next-line camelcase */}
																	{lang.t(
																		"dynamic.miles from job site",
																		{
																			smart_count:
																				point.distance.toFixed(
																					2,
																				),
																		},
																	)}
																</Otherwise>
															</Choose>
														</div>
													</Otherwise>
												</Choose>
											</div>
										</div>
									</For>
								</div>
							</div>
						</div>
					</If>
				</div>
			</Modal.Body>
			<Loader active={searchOfficerLoading} />
		</Modal>
	);
};

JobActivityLogModal.propTypes = {
	show: PropTypes.bool,
	onHide: PropTypes.func,
	clockInTime: PropTypes.string,
	clockOutTime: PropTypes.string,
	officer: PropTypes.object,
	user: PropTypes.object.isRequired,
	officerId: PropTypes.number,
	jobEventTimeZoneId: PropTypes.string.isRequired,
	positionId: PropTypes.number.isRequired,
	positionTitle: PropTypes.string.isRequired,
};

JobActivityLogModal.defaultProps = {
	show: false,
	onHide: () => {},
	clockInTime: "",
	clockOutTime: "",
	officer: undefined,
	officerId: undefined,
};

export default JobActivityLogModal;
